import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { getProfile } from '../../../../redux/modules/profile/selectors';
import TableCard from '../components/table-card';
import { getAssignedPrioritiesV2 } from '../../../../services/priority/priority.service';
import { mapToLegacy } from '../../../../utils/CommonUtils';
import { DOMAIN_ELEMENT_VARIENT } from '../../../provider-v2/member-detail/member-detail.constents';
import { Box } from '@mui/material';
import { colors, fontWeight, Text, textLevel } from '../../../../packages';
import { flattenExternalRxRecords } from '../../../../utils/medicationUtils';

const Medications = ({
  memberId,
  onViewAll,
  hideNotes,
  title = '',
  reset,
  onClick,
  isCardView = false,
  setCount = () => {},
}) => {
  const typeName = 'medications';
  const [loading, setLoading] = useState(true);
  const [records, setRecords] = useState([]);
  const { lookupData } = useSelector(getProfile);

  useEffect(() => {
    setLoading(true);
    if (memberId) {
      getAssignedPrioritiesV2({
        patientId: memberId,
        dataDomainType: typeName,
        pageNumber: 0,
        pageSize: 500,
      })
        .then(({ data }) => {
          // Flatten external RX records
          const flattenedRecords = flattenExternalRxRecords(data.records);

          // Filter flattened records to only show ACTIVE ones (removes inactive history items)
          const activeFlattenedRecords = flattenedRecords.filter(
            record =>
              record.currentImportanceLevel === 'ACTIVE' ||
              record.currentImportanceLevel === 'ACTIVE_EXTERNAL_RX',
          );

          // Map to legacy format for display
          setRecords(
            activeFlattenedRecords.map(record =>
              mapToLegacy(record, lookupData, DOMAIN_ELEMENT_VARIENT.MEDICATION),
            ),
          );

          // Set count based on active flattened records
          setCount(prev => ({ ...prev, activeMedications: activeFlattenedRecords.length }));
          setLoading(false);
        })
        .catch(e => {
          console.error(e);
          setLoading(false);
          setRecords([]);
        });
    }
  }, [memberId, reset]);

  const columns = [
    {
      id: 'name',
      label: 'Med Name',
    },
    {
      id: 'externalMedicationName',
      label: 'Drug',
    },
    {
      id: 'provider',
      label: 'Prescriber',
    },
    {
      id: 'sig',
      label: 'Sig',
    },
    {
      id: 'durationText',
      label: 'Duration',
    },
    {
      id: 'dispenseAmountText',
      label: 'Dispense Amount',
    },
    {
      id: 'updatedAt',
      label: 'Date prescribed',
    },
    {
      id: 'status',
      label: 'Status',
    },
  ];

  if (!loading && records.length === 0) {
    return null;
  }
  return (
    <>
      {isCardView ? (
        <Box display="flex" flexDirection="column" gap="4px">
          <Text level={textLevel.M} weight={fontWeight.MEDIUM} color={colors.neutral900}>
            {records
              .map(record => {
                // External RX: use note as drug name
                if (record.status?.toLowerCase().includes('external')) {
                  return record.note || record.name || 'Unknown';
                }
                // Regular med: use name
                return record.name;
              })
              .join(', ')}
          </Text>
          <Box display="flex" gap="1px">
            <Text level={textLevel.S} weight={fontWeight.MEDIUM} color={colors.neutral600}>
              {records
                .map(record => {
                  // External RX: use sig
                  if (record.status?.toLowerCase().includes('external')) {
                    return record.sig || 'Not specified';
                  }
                  // Regular med: use dosage and frequency
                  return `${record.dosage || ''}, ${record.frequency || ''}`;
                })
                .join(', ')}
            </Text>
          </Box>
        </Box>
      ) : (
        <TableCard
          columns={columns}
          data={records}
          loading={loading}
          title={title === '' ? 'Medications' : title}
          hideNotes={hideNotes}
          onRowClick={item => onClick(item, typeName)}
          action={{ onClick: () => onViewAll(typeName), label: 'View all' }}
        />
      )}
    </>
  );
};

export default Medications;
